import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, CheckCircle, XCircle, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

interface CallInvitationAcceptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  notificationId: string;
  callId: string;
  callTitle: string;
  callDescription?: string;
  inviterName: string;
  bountyAmount?: number;
  callType?: string;
}

export const CallInvitationAcceptDialog = ({
  open,
  onOpenChange,
  notificationId,
  callId,
  callTitle,
  callDescription,
  inviterName,
  bountyAmount,
  callType,
}: CallInvitationAcceptDialogProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const handleAccept = async () => {
    setIsProcessing(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create a call submission to indicate interest/acceptance
      const { error: submissionError } = await supabase
        .from('call_submissions')
        .insert({
          call_id: callId,
          submitter_id: user.id,
          status: 'accepted',
          submission_text: 'Accepted call invitation'
        });

      if (submissionError) throw submissionError;

      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      toast({
        title: "Call accepted!",
        description: "You've joined this call. You can now submit your contribution.",
      });

      onOpenChange(false);
      navigate(`/engage?call=${callId}`);

    } catch (error) {
      console.error('Error accepting call invitation:', error);
      toast({
        title: "Failed to accept call",
        description: "There was an error accepting the call. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!showDeclineForm) {
      setShowDeclineForm(true);
      return;
    }

    setIsProcessing(true);
    
    try {
      // Mark notification as read
      await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId);

      // Get inviter's user id from the call
      const { data: call } = await supabase
        .from('calls')
        .select('creator_id')
        .eq('id', callId)
        .single();

      const { data: { user } } = await supabase.auth.getUser();
      const { data: profile } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('id', user?.id)
        .single();

      // Notify the call creator about the decline
      if (call?.creator_id) {
        await supabase.from('notifications').insert({
          user_id: call.creator_id,
          type: 'call_invitation_declined',
          title: 'Call Invitation Declined',
          message: `${profile?.display_name || 'A user'} declined your call invitation${declineReason ? `: "${declineReason}"` : '.'}`,
          action_url: `/engage?call=${callId}`,
          metadata: {
            call_id: callId,
            decliner_name: profile?.display_name,
            decline_reason: declineReason
          }
        });
      }

      toast({
        title: "Invitation declined",
        description: "You've declined the call invitation.",
      });

      onOpenChange(false);

    } catch (error) {
      console.error('Error declining call invitation:', error);
      toast({
        title: "Failed to decline invitation",
        description: "There was an error processing your response. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    if (showDeclineForm) {
      setShowDeclineForm(false);
      setDeclineReason("");
    } else {
      onOpenChange(false);
    }
  };

  const getCallTypeLabel = (type?: string) => {
    switch (type) {
      case 'solutions': return 'Call for Solutions';
      case 'knowledge': return 'Call for Knowledge';
      case 'destruction': return 'Call for Destruction';
      default: return 'Call';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Call Invitation
          </DialogTitle>
          <DialogDescription>
            You've been invited to participate in a call
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">From:</p>
            <p className="text-base font-semibold">{inviterName}</p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Call:</p>
            <p className="text-base font-semibold">{callTitle}</p>
            {callType && (
              <Badge variant="secondary" className="text-xs">
                {getCallTypeLabel(callType)}
              </Badge>
            )}
          </div>

          {callDescription && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Description:</p>
              <p className="text-sm text-muted-foreground line-clamp-3">{callDescription}</p>
            </div>
          )}

          {bountyAmount && bountyAmount > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Bounty:</p>
              <p className="text-lg font-bold text-green-600">${bountyAmount.toLocaleString()}</p>
            </div>
          )}

          {showDeclineForm ? (
            <div className="space-y-2">
              <Label htmlFor="decline-reason">Reason for declining (optional)</Label>
              <Textarea
                id="decline-reason"
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                placeholder="Let the caller know why you're declining..."
                rows={3}
              />
            </div>
          ) : (
            <div className="pt-2">
              <p className="text-sm text-muted-foreground">
                Would you like to participate in this call and submit your contribution?
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          {showDeclineForm ? (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleDecline}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <XCircle className="mr-2 h-4 w-4" />
                )}
                Confirm Decline
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleDecline}
                disabled={isProcessing}
              >
                <XCircle className="mr-2 h-4 w-4" />
                No, Decline
              </Button>
              <Button
                onClick={handleAccept}
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                Yes, Accept Call
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};